import Button from "@app/components/button";
import Checkbox from "@app/components/checkbox";
import CustomAsyncSelect from "@app/components/custom-async-select";
import { PromiseOptionsCallback } from "@app/components/custom-async-select/custom-async-select.component";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import { addToast } from "@app/components/toast/toast.service";
import { RoutePath, SystemMessage } from "@app/constants";
import AdvertiseService from "@app/services/http/advertise.service";
import BookService from "@app/services/http/book.service";
import CategoryService from "@app/services/http/category.service";
import { LabelValue } from "@app/types";
import { CreateAdvertiseForm } from "@app/types/advertise.type";
import { createAdvertiseValidationSchema } from "@app/validations";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { Form, Formik, FormikContextType } from "formik";
import { createRef, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

const UpdateAdvertisePage = () => {
  const { advertiseId } = useParams();
  const navigate = useNavigate();
  const { abortController } = useCancellationRequest();

  const [update, forceUpdate] = useForceUpdate();

  const formRef = createRef<FormikContextType<CreateAdvertiseForm>>();

  useEffect(() => {
    if (isNaN(Number(advertiseId))) {
      return navigate(`/${RoutePath.ADVERTISE_MANAGEMENT}`);
    }

    AdvertiseService.get(Number(advertiseId)).then((res) => {
      const {
        script,
        duration,
        isFullScreen,
        canClose,
        bookId,
        categoryId,
        bookName,
        categoryName,
        navigateTo,
      } = res.data;

      formRef.current?.setValues({
        script,
        duration,
        isFullScreen,
        canClose,
        navigateTo,
        book: { label: bookName, value: bookId },
        category: { label: categoryName, value: categoryId },
      });
    });
  }, [advertiseId, update]);

  const onSubmit = (values: CreateAdvertiseForm) => {
    if (!formRef.current) return;

    const advertise = {
      script: values.script,
      duration: +values.duration,
      isFullScreen: !!values.isFullScreen,
      canClose: !!values.canClose,
      bookId: values.book?.value,
      categoryId: values.category?.value,
      navigateTo: values.navigateTo,
    };

    AdvertiseService.update(Number(advertiseId), advertise).then(() => {
      addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
      forceUpdate();
    });
  };

  const getBooksOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      BookService.getList(
        {
          keyword: inputValue.trim(),
        },
        abortController.signal
      ).then((res) => {
        const data: LabelValue[] = res.data.items
          .map((item) => ({ label: item.name, value: item.id }))
          .filter((item) => {
            if (inputValue.trim()) {
              return (
                item.label
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .indexOf(
                    inputValue
                      .trim()
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                  ) >= 0
              );
            }

            return true;
          });

        callback(data);
      });
    },
    []
  );

  const getCategoriesOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      CategoryService.getList(
        {
          keyword: inputValue.trim(),
        },
        abortController.signal
      ).then((res) => {
        const data: LabelValue[] = res.data.items
          .map((item) => ({ label: item.name, value: item.id }))
          .filter((item) => {
            if (inputValue.trim()) {
              return (
                item.label
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .indexOf(
                    inputValue
                      .trim()
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, "")
                  ) >= 0
              );
            }

            return true;
          });

        callback(data);
      });
    },
    []
  );

  const gotoListAdvertise = () => {
    navigate(`/${RoutePath.ADVERTISE_MANAGEMENT}`);
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center ">
        <div className="text-[#343C6A]">Danh sách Quảng cáo / Sửa</div>
      </div>
      <Formik
        innerRef={formRef}
        initialValues={{
          script: "",
          duration: "",
          isFullScreen: true,
          canClose: true,
          book: null,
          category: null,
          navigateTo: null,
        }}
        onSubmit={onSubmit}
        validationSchema={createAdvertiseValidationSchema}
        validateOnChange
        validateOnBlur
        enaleReinitialize
      >
        <Form>
          <div className="mt-6 rounded-xl bg-white py-6 px-5">
            <div className="w-[100%] max-w-[700px]">
              <FormControl name="script">
                <Input className="mt-7" placeholder="Script" />
              </FormControl>
              <FormControl name="duration">
                <Input
                  type="number"
                  className="mt-7"
                  placeholder="Khoảng thời gian"
                />
              </FormControl>
              <FormControl name="book">
                <CustomAsyncSelect
                  className="mt-7 rounded-lg"
                  promiseOptions={getBooksOptions}
                  placeholder="Book name"
                  styles={{
                    stylesControl: {
                      minHeight: "52px",
                    },
                  }}
                />
              </FormControl>
              <FormControl name="category">
                <CustomAsyncSelect
                  className="mt-7 rounded-lg"
                  promiseOptions={getCategoriesOptions}
                  placeholder="Category name"
                  styles={{
                    stylesControl: {
                      minHeight: "52px",
                    },
                  }}
                />
              </FormControl>
              <FormControl name="navigateTo">
                <Input className="mt-7" placeholder="Điều hướng" />
              </FormControl>
              <FormControl name="isFullScreen">
                <Checkbox
                  label="Toàn màn hình"
                  labelClassName="text-sm text-[#83898C]"
                  className="mt-7"
                />
              </FormControl>
              <FormControl name="canClose">
                <Checkbox
                  label="Có thể đóng"
                  labelClassName="text-sm text-[#83898C]"
                  className="mt-7"
                />
              </FormControl>
            </div>
          </div>
          <div className="mt-6 flex justify-left gap-4">
            <Button
              type="submit"
              size="xs"
              label={"Lưu"}
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
            />
            <Button
              size="xs"
              label="Hủy"
              theme="secondary"
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
              onClick={gotoListAdvertise}
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default UpdateAdvertisePage;
